(function () {
  const UK = new Set(['GB', 'UK', 'GG', 'JE', 'IM']);
  const EUROPE = new Set([
    'AL', 'AD', 'AM', 'AT', 'AZ', 'BY', 'BE', 'BA', 'BG', 'HR', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
    'IE', 'IT', 'XK', 'LV', 'LI', 'LT', 'LU', 'MD', 'MC', 'ME', 'NL', 'MK', 'NO', 'PL', 'PT', 'RO', 'RS',
    'SK', 'SI', 'ES', 'SE', 'CH', 'TR', 'UA', 'VA', 'SM', 'GI', 'FO', 'GL', 'AX', 'SJ', 'HU', 'CY', 'MT', 'IS',
  ]);

  const FREE_EUROPE = 'Free shipping to Europe';

  function getRegion(countryCode) {
    const code = (countryCode || '').toUpperCase();
    if (UK.has(code)) return 'uk';
    if (code === 'US') return 'us';
    if (EUROPE.has(code)) return 'europe';
    return 'other';
  }

  function getWeightKg(weight, unit) {
    const value = parseFloat(weight) || 0;
    switch ((unit || 'g').toLowerCase()) {
      case 'kg':
        return value;
      case 'g':
        return value / 1000;
      case 'lb':
        return value * 0.453592;
      case 'oz':
        return value * 0.0283495;
      default:
        return value / 1000;
    }
  }

  function formatMoney(amount, currency) {
    const locale = currency === 'GBP' ? 'en-GB' : currency === 'USD' ? 'en-US' : 'de-DE';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      if (currency === 'GBP') return `£${Math.round(amount)}`;
      if (currency === 'USD') return `$${Math.round(amount)}`;
      return `€${Math.round(amount)}`;
    }
  }

  function cleanCountryName(name) {
    return (name || '').replace(/\s*\([A-Z]{2}\)\s*$/, '').trim();
  }

  // --- Shipping rules (UK, US, Europe only) ---

  function calculateUkShipping(priceMajor, weightKg, priceCurrency) {
    const free =
      (priceCurrency === 'GBP' && priceMajor >= 399) ||
      (priceCurrency === 'EUR' && priceMajor >= 399);

    if (free) return { free: true, amount: 0, currency: 'GBP' };
    if (weightKg <= 49) return { free: false, amount: 25, currency: 'GBP' };
    return null;
  }

  function calculateUsShipping(weightKg) {
    if (weightKg < 10) return { free: false, amount: 39, currency: 'USD' };
    if (weightKg < 50) return { free: false, amount: 99, currency: 'USD' };
    if (weightKg < 80) return { free: false, amount: 199, currency: 'USD' };
    if (weightKg < 100) return { free: false, amount: 299, currency: 'USD' };
    if (weightKg < 160) return { free: false, amount: 299, currency: 'USD' };
    if (weightKg < 180) return { free: false, amount: 399, currency: 'USD' };
    if (weightKg < 200) return { free: false, amount: 499, currency: 'USD' };
    if (weightKg < 260) return { free: false, amount: 599, currency: 'USD' };
    if (weightKg < 300) return { free: false, amount: 799, currency: 'USD' };
    return { free: false, amount: 899, currency: 'USD' };
  }

  function calculateEuropeShipping(priceMajor, weightKg, priceCurrency) {
    if (priceCurrency === 'EUR' && priceMajor >= 399) {
      return { free: true, amount: 0, currency: 'EUR' };
    }
    if (weightKg <= 49) return { free: false, amount: 25, currency: 'EUR' };
    return null;
  }

  function calculateShipping(countryCode, weightKg, priceCents, priceCurrency) {
    const region = getRegion(countryCode);
    const priceMajor = (parseInt(priceCents, 10) || 0) / 100;

    if (region === 'uk') return calculateUkShipping(priceMajor, weightKg, priceCurrency);
    if (region === 'us') return calculateUsShipping(weightKg);
    if (region === 'europe') return calculateEuropeShipping(priceMajor, weightKg, priceCurrency);
    return null;
  }

  // --- Label text (same for price line + highlights unless noted) ---

  function buildShippingLabel(region, result, countryName) {
    if (region === 'other') {
      return { price: null, highlights: FREE_EUROPE, useCheckoutFallback: true };
    }

    if (region === 'europe') {
      if (result && !result.free) {
        const text = `Shipping estimate: ${formatMoney(result.amount, result.currency)}`;
        return { price: text, highlights: text };
      }
      return { price: FREE_EUROPE, highlights: FREE_EUROPE };
    }

    if (region === 'uk') {
      const ukName = cleanCountryName(countryName) || 'the United Kingdom';
      if (result?.free) {
        const text = `Free shipping to ${ukName}`;
        return { price: text, highlights: text };
      }
      if (result?.amount > 0) {
        const text = `Shipping estimate: ${formatMoney(result.amount, result.currency)}`;
        return { price: text, highlights: text };
      }
      const text = `Free shipping to ${ukName}`;
      return { price: text, highlights: text };
    }

    if (region === 'us') {
      const usName = cleanCountryName(countryName) || 'the United States';
      if (result?.free) {
        const text = `Free shipping to ${usName}`;
        return { price: text, highlights: text };
      }
      if (result?.amount > 0) {
        const text = `Shipping estimate: ${formatMoney(result.amount, result.currency)}`;
        return { price: text, highlights: text };
      }
      return { price: null, highlights: null };
    }

    return { price: null, highlights: FREE_EUROPE, useCheckoutFallback: true };
  }

  // --- Variant + country data ---

  function getSelectedVariant(productInfo) {
    if (productInfo._activeVariant) return productInfo._activeVariant;

    const json = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
    if (json) {
      try {
        return JSON.parse(json);
      } catch (error) {
        /* continue */
      }
    }

    const variantsScript = productInfo.querySelector('[data-buso-shipping-variants]');
    if (!variantsScript?.textContent) return null;

    try {
      const variants = JSON.parse(variantsScript.textContent);
      const variantId = productInfo.querySelector('input[name="id"]')?.value;
      const match = variants.find((variant) => String(variant.id) === String(variantId));
      return match || (variants.length === 1 ? variants[0] : null);
    } catch (error) {
      return null;
    }
  }

  function getCountry(productInfo) {
    return {
      code: productInfo.dataset.shippingCountry || '',
      name: productInfo.dataset.shippingCountryName || '',
    };
  }

  function setElementText(element, text, useFallbackHtml) {
    if (!text && !useFallbackHtml) {
      element.hidden = true;
      element.textContent = '';
      return;
    }

    element.hidden = false;
    if (useFallbackHtml) {
      if (!element.dataset.fallbackHtml) {
        element.dataset.fallbackHtml = element.innerHTML.trim();
      }
      element.innerHTML = element.dataset.fallbackHtml;
    } else {
      element.textContent = text;
    }
  }

  function updateShipping(productInfo) {
    const priceEl = productInfo.querySelector('[data-buso-shipping-display][data-display-context="price"]');
    const highlightsEl = productInfo.querySelector('[data-buso-shipping-display][data-display-context="highlights"]');
    if (!priceEl && !highlightsEl) return;

    const country = getCountry(productInfo);
    const region = getRegion(country.code);
    const variant = getSelectedVariant(productInfo);
    const priceCurrency = productInfo.dataset.priceCurrency || 'EUR';

    let result = null;
    if (variant) {
      result = calculateShipping(
        country.code,
        getWeightKg(variant.weight, variant.weight_unit),
        variant.price,
        priceCurrency
      );
    }

    const labels = buildShippingLabel(region, result, country.name);

    if (priceEl) {
      setElementText(priceEl, labels.price, labels.useCheckoutFallback);
    }

    if (highlightsEl) {
      setElementText(highlightsEl, labels.highlights, false);
    }
  }

  function initProductInfo(productInfo) {
    if (!productInfo.dataset.shippingCountry || productInfo.dataset.busoShippingInit === 'true') return;
    productInfo.dataset.busoShippingInit = 'true';

    updateShipping(productInfo);

    productInfo.addEventListener('product-info:loaded', () => updateShipping(productInfo));

    productInfo.querySelector('input[name="id"]')?.addEventListener('change', () => {
      productInfo._activeVariant = null;
      updateShipping(productInfo);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (productInfo.sectionId !== event?.data?.sectionId) return;
        productInfo._activeVariant = event.data.variant || null;
        updateShipping(productInfo);
      });
    }
  }

  function initAll() {
    document.querySelectorAll('product-info[data-shipping-country]').forEach(initProductInfo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
